import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  UserPlus,
  Shield,
  UserCheck,
  UserX,
  Clock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const AccessControl = () => {
  const [authorizedAgents, setAuthorizedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAuthorizedAgents();
  }, []);

  const fetchAuthorizedAgents = async () => {
    try {
      const response = await axios.get(`${API}/authorized-agents`, { withCredentials: true });
      setAuthorizedAgents(response.data);
    } catch (error) {
      console.error("Error fetching authorized agents:", error);
      toast.error("Failed to fetch authorized agents");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error("Email is required");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/authorized-agents`, {
        email: newEmail.trim(),
        name: newName.trim()
      }, { withCredentials: true });
      
      toast.success(`${newEmail} has been authorized`);
      setNewEmail("");
      setNewName("");
      setDialogOpen(false);
      fetchAuthorizedAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add authorized agent");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAgent = async (email) => {
    if (!confirm(`Are you sure you want to remove ${email} from authorized list?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/authorized-agents/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`${email} has been removed`);
      fetchAuthorizedAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove authorized agent");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="access-control-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif">Access Control</h2>
          <p className="text-muted-foreground text-sm">
            Manage who can sign in to the system. Only authorized emails can access the dashboard.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-authorized-agent-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Authorized Email
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Authorize New Agent</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddAgent} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email Address *</label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="agent@example.com"
                  required
                  data-testid="authorized-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name (Optional)</label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Agent Name"
                  data-testid="authorized-name-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Once added, this email will be able to sign in and access the dashboard.
              </p>
              <div className="flex gap-3">
                <Button type="submit" className="flex-1" disabled={submitting} data-testid="submit-authorized-agent-btn">
                  {submitting ? "Adding..." : "Authorize Email"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-muted text-sm font-medium">
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Added</div>
          <div className="col-span-1"></div>
        </div>
        
        {authorizedAgents.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No authorized agents yet.</p>
            <p className="text-sm">Add email addresses to allow agents to sign in.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {authorizedAgents.map((agent) => (
              <div key={agent.email} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-muted/50">
                <div className="col-span-4 flex items-center gap-2">
                  {agent.has_signed_in ? (
                    <UserCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <Clock className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="font-medium truncate">{agent.email}</span>
                </div>
                <div className="col-span-2 text-muted-foreground truncate">
                  {agent.user?.name || agent.name || "-"}
                </div>
                <div className="col-span-2">
                  {agent.status === "active" && (
                    <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">Active</Badge>
                  )}
                  {agent.status === "deactivated" && (
                    <Badge variant="destructive">Deactivated</Badge>
                  )}
                  {agent.status === "pending" && (
                    <Badge variant="secondary">Not Signed In</Badge>
                  )}
                </div>
                <div className="col-span-2">
                  <Badge variant="outline" className="capitalize">
                    {agent.role || "pending"}
                  </Badge>
                </div>
                <div className="col-span-1 text-xs text-muted-foreground">
                  {agent.added_at ? new Date(agent.added_at).toLocaleDateString() : "-"}
                </div>
                <div className="col-span-1 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAgent(agent.email)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`remove-agent-${agent.email}`}
                  >
                    <UserX className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-muted/50 border border-border rounded-sm p-4">
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <Shield className="w-4 h-4" />
          How Access Control Works
        </h3>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>Only emails in the authorized list can sign in to the dashboard</li>
          <li>Unauthorized users will see an "Access Denied" message</li>
          <li>You cannot remove your own email from the list</li>
          <li>New authorized agents automatically get the "Agent" role</li>
        </ul>
      </div>
    </div>
  );
};

export default AccessControl;
